import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Envelope, Phone, WhatsappLogo, PaperPlaneTilt, MagnifyingGlass, House,
  ArrowLeft, CheckCircle, Clock, Handshake, Trophy, XCircle, DotsThreeVertical,
  ChatCircleText, EnvelopeOpen, User, ArrowClockwise, MapPin,
} from "@phosphor-icons/react";

const STATUS_META = {
  nuevo:           { label: "Nuevo",           icon: Clock,      chip: "bg-[#C8A24A]/10 text-[#C8A24A] border-[#C8A24A]/40", dot: "bg-[#C8A24A]" },
  contactado:      { label: "Contactado",      icon: ChatCircleText, chip: "bg-sky-500/10 text-sky-300 border-sky-500/40", dot: "bg-sky-500" },
  visita:          { label: "Visita",          icon: MapPin,     chip: "bg-violet-500/10 text-violet-300 border-violet-500/40", dot: "bg-violet-500" },
  en_negociacion:  { label: "En negociación",  icon: Handshake,  chip: "bg-amber-500/10 text-amber-300 border-amber-500/40", dot: "bg-amber-500" },
  ganado:          { label: "Ganado",          icon: Trophy,     chip: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40", dot: "bg-emerald-500" },
  perdido:         { label: "Perdido",         icon: XCircle,    chip: "bg-zinc-500/10 text-zinc-300 border-zinc-500/40", dot: "bg-zinc-500" },
};

const STATUS_FLOW = ["nuevo", "contactado", "visita", "en_negociacion", "ganado", "perdido"];

const FILTER_TABS = [
  { key: "todas", label: "Todas" },
  ...STATUS_FLOW.map(s => ({ key: s, label: STATUS_META[s].label })),
];

const SORT_OPTIONS = [
  { key: "newest",   label: "Más recientes" },
  { key: "oldest",   label: "Más antiguas" },
  { key: "activity", label: "Última actividad" },
];

const digitsOnly = (s) => (s || "").replace(/[^0-9+]/g, "");

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export function LeadCenter() {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("todas");
  const [readFilter, setReadFilter] = useState("all"); // all | unread
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "todas") params.status = statusFilter;
      if (readFilter === "unread") params.read = "false";
      if (sortBy) params.sort_by = sortBy;
      if (search.trim()) params.q = search.trim();
      const [listRes, sumRes] = await Promise.all([
        api.get("/inquiries", { params }),
        api.get("/inquiries/summary").catch(() => ({ data: null })),
      ]);
      setInquiries(listRes.data);
      setSummary(sumRes.data);
      // Preserve selection if still in list.
      if (selectedId && !listRes.data.find(i => i.id === selectedId)) {
        setSelectedId(listRes.data[0]?.id ?? null);
      } else if (!selectedId && listRes.data.length > 0) {
        setSelectedId(listRes.data[0].id);
      }
    } catch (err) {
      toast.error(formatApiError(err, "No pudimos cargar las consultas"));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [statusFilter, readFilter, sortBy]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadAll(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const selected = useMemo(() => inquiries.find(i => i.id === selectedId) || null, [inquiries, selectedId]);

  const patchLocal = (id, patch) => setInquiries(list => list.map(i => i.id === id ? { ...i, ...patch } : i));

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}/status`, { status });
      patchLocal(id, data);
      toast.success(`Estado actualizado`);
      // Refresh summary counts
      const { data: sum } = await api.get("/inquiries/summary").catch(() => ({ data: summary }));
      setSummary(sum);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleRead = async (id, current) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}/read`, { read: !current });
      patchLocal(id, data);
      const { data: sum } = await api.get("/inquiries/summary").catch(() => ({ data: summary }));
      setSummary(sum);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markReadIfUnread = async (id) => {
    const inq = inquiries.find(i => i.id === id);
    if (inq && !inq.read) {
      try {
        const { data } = await api.patch(`/inquiries/${id}/read`, { read: true });
        patchLocal(id, data);
        const { data: sum } = await api.get("/inquiries/summary").catch(() => ({ data: summary }));
        setSummary(sum);
      } catch { /* ignore */ }
    }
  };

  const addNote = async (id, text) => {
    const { data } = await api.post(`/inquiries/${id}/notes`, { text });
    patchLocal(id, data);
    return data;
  };

  const kpi = summary?.by_status || {};

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10" data-testid="lead-center-page">
      {/* Workspace header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <Link to="/dashboard" className="text-xs text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1" data-testid="lead-back-link">
            <ArrowLeft size={12} /> Panel
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold mt-2">Centro comercial</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-1 tracking-tight" data-testid="lead-center-title">
            Bandeja de leads
          </h1>
          <p className="text-sm text-[#C8A24A]/70 mt-2">Tu espacio de trabajo comercial. Prioriza, contacta y cierra.</p>
        </div>
        <Button variant="outline" onClick={loadAll} data-testid="lead-refresh-btn" className="h-11">
          <ArrowClockwise size={14} className="mr-1" /> Actualizar
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        <KpiPill label="Total"           value={summary?.total ?? 0}          tint="gold"    testId="lead-kpi-total" />
        <KpiPill label="Sin leer"        value={summary?.unread ?? 0}         tint="gold"    testId="lead-kpi-unread" />
        <KpiPill label="Nuevos"          value={kpi.nuevo ?? 0}               tint="gold"    testId="lead-kpi-nuevo" />
        <KpiPill label="Contactados"     value={kpi.contactado ?? 0}          tint="sky"     testId="lead-kpi-contactado" />
        <KpiPill label="En negociación"  value={kpi.en_negociacion ?? 0}      tint="amber"   testId="lead-kpi-neg" />
        <KpiPill label="Ganados"         value={kpi.ganado ?? 0}              tint="emerald" testId="lead-kpi-ganado" />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex flex-wrap gap-1 bg-[#111111] border border-[#C8A24A]/20 rounded-xl p-1" data-testid="lead-status-tabs">
          {FILTER_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              data-testid={`lead-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === t.key
                  ? "bg-[#C8A24A] text-[#111111]"
                  : "text-[#C8A24A]/70 hover:text-[#C8A24A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setReadFilter(readFilter === "unread" ? "all" : "unread")}
          data-testid="lead-unread-toggle"
          className={`h-11 px-4 rounded-lg text-xs font-medium border transition-colors ${
            readFilter === "unread"
              ? "border-[#C8A24A] bg-[#C8A24A]/10 text-[#C8A24A]"
              : "border-[#C8A24A]/20 text-[#C8A24A]/70 hover:border-[#C8A24A]/40"
          }`}
        >
          Solo sin leer
        </button>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="lead-sort-select"
          className="h-11 bg-[#111111] border border-[#C8A24A]/20 rounded-lg px-3 text-xs text-[#C8A24A]/90"
        >
          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A24A]/60" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, correo o mensaje"
            className="pl-9"
            data-testid="lead-search-input"
          />
        </div>
      </div>

      {/* Main workspace grid */}
      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        {/* List */}
        <div className="bg-[#111111] rounded-2xl border border-[#C8A24A]/20 overflow-hidden max-h-[70vh] lg:max-h-[75vh] flex flex-col" data-testid="lead-list-column">
          <div className="px-4 py-3 border-b border-[#C8A24A]/10 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">
              {inquiries.length} consulta(s)
            </span>
          </div>
          {loading ? (
            <div className="p-4 space-y-3">
              {[0,1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-[#1A1A1A] animate-pulse" />)}
            </div>
          ) : inquiries.length === 0 ? (
            <div className="p-10 text-center flex-1 flex flex-col items-center justify-center" data-testid="lead-empty-list">
              <Envelope size={40} className="text-[#C8A24A]/30 mb-3" />
              <p className="font-heading text-lg">Sin consultas</p>
              <p className="text-xs text-[#C8A24A]/60 mt-1">Cuando un comprador te escriba, aparecerá aquí.</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 divide-y divide-[#C8A24A]/5">
              {inquiries.map(inq => (
                <LeadListItem
                  key={inq.id}
                  inq={inq}
                  active={inq.id === selectedId}
                  onClick={() => { setSelectedId(inq.id); markReadIfUnread(inq.id); }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="bg-[#111111] rounded-2xl border border-[#C8A24A]/20 min-h-[400px] lg:max-h-[75vh] overflow-y-auto" data-testid="lead-detail-column">
          {selected ? (
            <LeadDetail
              inquiry={selected}
              onChangeStatus={(s) => changeStatus(selected.id, s)}
              onToggleRead={() => toggleRead(selected.id, selected.read)}
              onAddNote={(text) => addNote(selected.id, text)}
            />
          ) : (
            <div className="p-16 text-center" data-testid="lead-empty-detail">
              <ChatCircleText size={48} className="mx-auto text-[#C8A24A]/30 mb-4" />
              <p className="font-heading text-xl">Selecciona una consulta</p>
              <p className="text-sm text-[#C8A24A]/60 mt-2">Elige un lead a la izquierda para ver detalles, tomar acción y registrar seguimiento.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadListItem({ inq, active, onClick }) {
  const meta = STATUS_META[inq.status] || STATUS_META.nuevo;
  const initials = (inq.name || "??").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`lead-item-${inq.id}`}
      className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors ${
        active ? "bg-[#C8A24A]/10 border-l-2 border-[#C8A24A]" : "border-l-2 border-transparent hover:bg-[#C8A24A]/[0.04]"
      }`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        inq.read ? "bg-[#1A1A1A] text-[#C8A24A]/70 border border-[#C8A24A]/20" : "bg-[#C8A24A] text-black"
      }`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {!inq.read && <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A]" data-testid={`lead-unread-dot-${inq.id}`} />}
          <p className={`text-sm truncate ${inq.read ? "text-[#C8A24A]/80" : "text-[#C8A24A] font-semibold"}`}>{inq.name}</p>
        </div>
        <p className="text-xs text-[#C8A24A]/60 truncate mt-0.5">{inq.property_title}</p>
        <p className="text-xs text-[#C8A24A]/50 truncate mt-0.5">{inq.message}</p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className={`text-xs uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${meta.chip}`}>
          {meta.label}
        </span>
        <span className="text-xs text-[#C8A24A]/50">
          {new Date(inq.last_activity_at || inq.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
        </span>
      </div>
    </button>
  );
}

function LeadDetail({ inquiry, onChangeStatus, onToggleRead, onAddNote }) {
  const [noteText, setNoteText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[inquiry.status] || STATUS_META.nuevo;
  const Icon = meta.icon;

  const waHref = inquiry.phone
    ? `https://wa.me/${digitsOnly(inquiry.phone).replace(/^\+/, "")}?text=${encodeURIComponent(`Hola ${inquiry.name}, te escribo respecto a tu interés en "${inquiry.property_title}".`)}`
    : null;
  const telHref = inquiry.phone ? `tel:${digitsOnly(inquiry.phone)}` : null;
  const mailHref = `mailto:${inquiry.email}?subject=${encodeURIComponent(`Re: ${inquiry.property_title}`)}&body=${encodeURIComponent(`Hola ${inquiry.name},%0D%0A%0D%0A`)}`;

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try { await onAddNote(noteText.trim()); setNoteText(""); toast.success("Nota agregada"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await onAddNote(`Respuesta enviada:\n${replyText.trim()}`);
      // Open mailto as a fallback for actual delivery
      window.open(`mailto:${inquiry.email}?subject=${encodeURIComponent(`Re: ${inquiry.property_title}`)}&body=${encodeURIComponent(replyText.trim())}`, "_blank");
      setReplyText("");
      toast.success("Respuesta registrada y correo abierto");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 sm:p-6" data-testid={`lead-detail-${inquiry.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap pb-5 border-b border-[#C8A24A]/10">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border ${meta.chip}`} data-testid={`lead-detail-status-badge`}>
              <Icon size={11} weight="fill" />
              {meta.label}
            </span>
            {!inquiry.read && (
              <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#C8A24A]/70 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A]" /> Sin leer
              </span>
            )}
          </div>
          <h2 className="font-heading text-2xl mt-2 flex items-center gap-2" data-testid="lead-detail-name">
            <User size={20} weight="duotone" className="text-[#C8A24A]" />
            {inquiry.name}
          </h2>
          <div className="text-xs text-[#C8A24A]/70 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>{inquiry.email}</span>
            {inquiry.phone && <span>· {inquiry.phone}</span>}
            <span>· Recibido {formatDateTime(inquiry.created_at)}</span>
          </div>
          <div className="mt-2 text-sm text-[#C8A24A] flex items-center gap-1">
            <House size={14} weight="fill" /> {inquiry.property_title}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onToggleRead} data-testid="lead-toggle-read-btn">
          {inquiry.read ? <><Envelope size={14} className="mr-1" /> Marcar sin leer</> : <><EnvelopeOpen size={14} className="mr-1" /> Marcar leído</>}
        </Button>
      </div>

      {/* Quick contact actions */}
      <div className="grid grid-cols-3 gap-2 my-5">
        <ContactButton
          href={waHref}
          disabled={!waHref}
          testId="lead-wa-btn"
          icon={WhatsappLogo}
          label="WhatsApp"
          onClick={() => onAddNote("Contactó por WhatsApp").catch(() => {})}
          color="emerald"
        />
        <ContactButton
          href={telHref}
          disabled={!telHref}
          testId="lead-call-btn"
          icon={Phone}
          label="Llamar"
          onClick={() => onAddNote("Llamada iniciada").catch(() => {})}
          color="sky"
        />
        <ContactButton
          href={mailHref}
          testId="lead-mail-btn"
          icon={Envelope}
          label="Correo"
          onClick={() => onAddNote("Correo abierto").catch(() => {})}
          color="gold"
        />
      </div>

      {/* Status flow */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Cambiar estado</p>
        <div className="flex flex-wrap gap-2" data-testid="lead-status-flow">
          {STATUS_FLOW.map(s => {
            const m = STATUS_META[s];
            const active = s === inquiry.status;
            return (
              <button
                key={s}
                type="button"
                onClick={() => !active && onChangeStatus(s)}
                data-testid={`lead-status-btn-${s}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? `${m.chip} cursor-default`
                    : "border-[#C8A24A]/20 text-[#C8A24A]/70 hover:border-[#C8A24A]/50 hover:text-[#C8A24A]"
                }`}
                disabled={active}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Original message */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Mensaje original</p>
        <div className="rounded-xl bg-[#0A0A0A] border border-[#C8A24A]/10 p-4 text-sm text-[#C8A24A]/90 whitespace-pre-wrap" data-testid="lead-detail-message">
          {inquiry.message}
        </div>
      </div>

      {/* Quick reply */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Respuesta rápida</p>
        <Textarea
          rows={3}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Redacta una respuesta breve..."
          data-testid="lead-reply-input"
        />
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            onClick={submitReply}
            disabled={busy || !replyText.trim()}
            className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]"
            data-testid="lead-reply-submit-btn"
          >
            <PaperPlaneTilt size={14} className="mr-1" /> Enviar y registrar
          </Button>
        </div>
      </div>

      {/* Internal notes + history */}
      <div>
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Notas y seguimiento</p>
        <div className="flex gap-2 mb-3">
          <Input
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), submitNote())}
            placeholder="Agrega una nota interna (no la ve el cliente)"
            data-testid="lead-note-input"
          />
          <Button variant="outline" onClick={submitNote} disabled={busy || !noteText.trim()} data-testid="lead-note-add-btn">
            Agregar
          </Button>
        </div>
        {(inquiry.notes || []).length === 0 ? (
          <p className="text-xs text-[#C8A24A]/50 italic" data-testid="lead-notes-empty">Aún no hay notas ni movimientos.</p>
        ) : (
          <ol className="relative border-l border-[#C8A24A]/15 ml-2 space-y-3" data-testid="lead-notes-list">
            {[...inquiry.notes].reverse().map((n) => (
              <li key={n.id} className="pl-4 relative" data-testid={`lead-note-${n.id}`}>
                <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${n.type === "status_change" ? "bg-[#C8A24A]" : "bg-[#C8A24A]/40"}`} />
                <div className="text-xs uppercase tracking-widest text-[#C8A24A]/50 font-semibold flex items-center gap-2">
                  <span>{n.type === "status_change" ? "Cambio de estado" : "Nota"}</span>
                  <span>·</span>
                  <span>{formatDateTime(n.created_at)}</span>
                </div>
                <p className="text-sm text-[#C8A24A]/90 mt-0.5 whitespace-pre-wrap">{n.text}</p>
                {n.author_name && <p className="text-xs text-[#C8A24A]/40 mt-0.5">— {n.author_name}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function ContactButton({ href, disabled, testId, icon: Icon, label, onClick, color }) {
  const colorMap = {
    emerald: "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10",
    sky:     "border-sky-500/40 text-sky-300 hover:bg-sky-500/10",
    gold:    "border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10",
  };
  const cls = colorMap[color] || colorMap.gold;
  if (disabled) {
    return (
      <button
        type="button"
        disabled
        data-testid={testId}
        className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-[#C8A24A]/10 text-[#C8A24A]/30 cursor-not-allowed"
      >
        <Icon size={20} weight="duotone" />
        <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
        <span className="text-xs opacity-60">No disponible</span>
      </button>
    );
  }
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl border transition-colors ${cls}`}
    >
      <Icon size={20} weight="duotone" />
      <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
    </a>
  );
}

function KpiPill({ label, value, tint = "gold", testId }) {
  const tintMap = {
    gold:    "text-[#C8A24A] border-[#C8A24A]/20",
    sky:     "text-sky-300 border-sky-500/20",
    amber:   "text-amber-300 border-amber-500/20",
    emerald: "text-emerald-300 border-emerald-500/20",
  };
  return (
    <div className={`p-3 rounded-xl bg-[#1A1A1A] border ${tintMap[tint] || tintMap.gold}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-[#C8A24A]/60 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold mt-0.5">{value}</p>
    </div>
  );
}
